import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { memberService, ledgerService } from "@/services";
import type { Member, MemberConsolidatedStatement, MemberStatement } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { Select } from "@/components/base/Select";
import { GroupStatementPrint } from "./components/GroupStatementPrint";
import { ConsolidatedStatementPrint } from "./components/ConsolidatedStatementPrint";
import { exportReportCSV, csvAmount } from "@/utils/report";
import { formatDate } from "@/utils/format";

type Mode = "group" | "consolidated";

export default function MemberStatementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMemberId = searchParams.get("memberId") || "";
  const urlGroupId = searchParams.get("groupId") || "";
  const urlMode = (searchParams.get("mode") as Mode) || "group";

  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [memberId, setMemberId] = useState(urlMemberId);
  const [groupId, setGroupId] = useState(urlGroupId);
  const [mode, setMode] = useState<Mode>(urlMode);
  const [statement, setStatement] = useState<MemberStatement | null>(null);
  const [consolidated, setConsolidated] = useState<MemberConsolidatedStatement | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await memberService.listMembers();
      setMembers(list);
      setMemberId((prev) => {
        const fromUrl = searchParams.get("memberId");
        if (fromUrl && list.some((m) => m.id === fromUrl)) return fromUrl;
        return prev && list.some((m) => m.id === prev) ? prev : (list[0]?.id || "");
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load members.");
      setMembers([]);
    }
  }, [searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedMember = members?.find((m) => m.id === memberId);
  const groupOptions = useMemo(() => {
    if (!selectedMember) return [];
    return selectedMember.enrollments
      .filter((e) => e.status === "active" || e.status === "completed")
      .map((e) => ({ value: e.groupId, label: `${e.groupName} — ticket ${e.ticketNumber}` }));
  }, [selectedMember]);

  // Reset / sync the group when the member changes.
  useEffect(() => {
    if (selectedMember) {
      const validEnrollments = selectedMember.enrollments.filter((e) => e.status === "active" || e.status === "completed");
      const urlGId = searchParams.get("groupId");
      setGroupId((prev) => {
        if (urlGId && validEnrollments.some((e) => e.groupId === urlGId)) {
          return urlGId;
        }
        const stillValid = validEnrollments.some((e) => e.groupId === prev);
        return stillValid ? prev : (validEnrollments[0]?.groupId ?? "");
      });
    }
  }, [selectedMember, searchParams]);

  const fetchStatement = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === "group") {
        if (!groupId) throw new Error("Select a group to view the statement.");
        setConsolidated(null);
        setStatement(await ledgerService.getMemberStatement(memberId, groupId));
      } else {
        setStatement(null);
        setConsolidated(await ledgerService.getMemberConsolidatedStatement(memberId));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the statement.");
      setStatement(null);
      setConsolidated(null);
    } finally {
      setLoading(false);
    }
  }, [memberId, groupId, mode]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const handleExport = () => {
    if (mode === "group" && statement) {
      const g = statement.group;
      exportReportCSV({
        filename: `statement-${statement.memberCode}-${g.groupCode}`,
        reportName: `Member Statement — ${statement.memberName} (${g.groupName})`,
        filters: [`Member ${statement.memberCode}`, `Group ${g.groupCode}`, `Ticket ${g.ticketNumber}`],
        columns: [
          { header: "Cycle", value: (r) => r.cycle },
          { header: "Period", value: (r) => r.period },
          { header: "Installment", value: (r) => csvAmount(r.installment) },
          { header: "Dividend", value: (r) => csvAmount(r.dividend) },
          { header: "Commission", value: (r) => csvAmount(r.commission) },
          { header: "Net payable", value: (r) => csvAmount(r.net) },
          { header: "Paid", value: (r) => csvAmount(r.paid) },
          { header: "Balance", value: (r) => csvAmount(r.balance) },
          { header: "Payment date", value: (r) => r.date },
          { header: "Receipt", value: (r) => r.receipt },
        ],
        rows: g.cycles.map((c) => ({
          cycle: c.cycle,
          period: c.periodLabel,
          installment: c.baseInstallment,
          dividend: c.dividendCredit,
          commission: c.commissionPerMember ?? 0,
          net: c.netPayable,
          paid: c.paid,
          balance: c.balance,
          date: c.paymentDate ? formatDate(c.paymentDate) : "",
          receipt: c.receiptNumber ?? "",
        })),
      });
    } else if (consolidated) {
      exportReportCSV({
        filename: `statement-${consolidated.memberCode}-consolidated`,
        reportName: `Consolidated Statement — ${consolidated.memberName}`,
        filters: ["All groups"],
        columns: [
          { header: "Group", value: (r) => r.group },
          { header: "Code", value: (r) => r.code },
          { header: "Ticket", value: (r) => r.ticket },
          { header: "Charged", value: (r) => csvAmount(r.charged) },
          { header: "Dividend", value: (r) => csvAmount(r.dividend) },
          { header: "Paid", value: (r) => csvAmount(r.paid) },
          { header: "Outstanding", value: (r) => csvAmount(r.outstanding) },
          { header: "Won cycle", value: (r) => r.won },
          { header: "Cycles left", value: (r) => r.left },
        ],
        rows: consolidated.groups.map((g) => ({
          group: g.groupName,
          code: g.groupCode,
          ticket: g.ticketNumber,
          charged: g.totalCharged,
          dividend: g.totalDividend,
          paid: g.totalPaid,
          outstanding: g.totalOutstanding,
          won: g.wonCycle != null ? g.wonCycle : "",
          left: g.cyclesRemaining,
        })),
      });
    }
  };

  const modeButton = (value: Mode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`cursor-pointer rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${mode === value ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-100"}`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <PageHeader
        className="no-print"
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Member statements" isLast />
          </>
        }
        title="Member statements"
        subtitle="The document members ask for when they want to know exactly where they stand."
      />

      <Card className="no-print">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-500">Member</span>
            <Select
              value={memberId}
              onChange={setMemberId}
              options={(members ?? []).map((m) => ({ value: m.id, label: `${m.fullName} — ${m.memberCode}` }))}
              placeholder="Select member"
              className="w-72"
            />
          </div>
          {mode === "group" ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-foreground-500">Group</span>
              <Select
                value={groupId}
                onChange={setGroupId}
                options={groupOptions}
                placeholder="Select group"
                className="w-80"
              />
            </div>
          ) : (
            <div className="flex h-9 items-center text-sm text-foreground-500">
              Consolidated across all {consolidated?.groups.length ?? 0} group(s)
            </div>
          )}
          <div className="flex flex-1 items-center justify-end gap-2">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {modeButton("group", "Group-wise")}
              {modeButton("consolidated", "Consolidated")}
            </div>
          </div>
        </div>
      </Card>

      {error ? <div className="mt-3"><ErrorState message={error} onRetry={fetchStatement} /></div> : null}

      {!loading && !error && mode === "group" && statement ? (
        <div className="mt-3">
          <PrintLayout
            title={`Member Statement — ${statement.group.groupName}`}
            meta={
              <>
                {statement.memberName} · {statement.memberCode} · Ticket {statement.group.ticketNumber} · Generated {formatDate(statement.generatedAt)}
              </>
            }
            actions={
              <Button size="sm" variant="outline" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
                Export
              </Button>
            }
          >
            <GroupStatementPrint statement={statement} />
          </PrintLayout>
        </div>
      ) : null}

      {!loading && !error && mode === "consolidated" && consolidated ? (
        <div className="mt-3">
          <PrintLayout
            title={`Consolidated Statement — ${consolidated.memberName}`}
            meta={`${consolidated.memberCode} · ${consolidated.groups.length} group(s) · Generated ${formatDate(consolidated.generatedAt)}`}
            actions={
              <Button size="sm" variant="outline" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
                Export
              </Button>
            }
          >
            <ConsolidatedStatementPrint statement={consolidated} />
          </PrintLayout>
        </div>
      ) : null}

      {!loading && !error && mode === "group" && !statement && !groupId ? (
        <div className="mt-3">
          <Card>
            <EmptyState icon="ri-file-user-line" title="Select a member and group" description="Choose a member above to view their group-wise statement." />
          </Card>
        </div>
      ) : null}

      {loading ? <div className="mt-3 h-64 animate-pulse rounded-lg bg-background-200" /> : null}
    </div>
  );
}